/**
 * Utility functions for formatting values in the application
 */

import { readCachedSystemConfig } from "../shared/lib/systemConfig";

/**
 * Convert API numeric-like value into a safe number.
 * Accepts numbers and formatted strings like "100,000.00".
 * @param {number|string|null|undefined} value
 * @param {number} fallback
 * @returns {number}
 */
export const toApiNumber = (value, fallback = 0) => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : fallback;
    }
    if (typeof value === "string") {
        const normalized = value
            .trim()
            .replace(/,/g, "")
            .replace(/[^\d.-]/g, "");
        if (!normalized) return fallback;
        const parsed = Number.parseFloat(normalized);
        return Number.isFinite(parsed) ? parsed : fallback;
    }
    return fallback;
};

/**
 * Snap a final payable total to the cash denomination configured in settings.
 * Intended for grand totals only — line prices / subtotals / commission stay exact.
 * Rounds UP to the next `step` only when the amount is at least `threshold`
 * over the lower multiple, otherwise stays on the lower multiple.
 *   step 250, threshold 25:  1525 -> 1750,  1524 -> 1500
 * step/threshold default to the system settings (250 / 25).
 * @param {number} value
 * @param {number} [step]
 * @param {number} [threshold]
 * @returns {number}
 */
export const roundToIqdDenomination = (value, step, threshold) => {
    const n = toApiNumber(value, 0);
    if (n <= 0) return 0;
    const cfg = readCachedSystemConfig();
    const s = Number.isFinite(step) && step > 0 ? step : (cfg?.pos_rounding_step > 0 ? cfg.pos_rounding_step : 250);
    const th = Number.isFinite(threshold) && threshold >= 0 ? threshold : (Number.isFinite(cfg?.pos_rounding_threshold) ? cfg.pos_rounding_threshold : 25);
    const floor = Math.floor(n / s) * s;
    return (n - floor) >= th ? floor + s : floor;
};

/**
 * Real product line amount (price × qty/weight). Never use customer total − commission:
 * that injects denomination rounding into the "subtotal before commission".
 */
export const orderProductLineAmount = (item = {}) => {
    const price = toApiNumber(item.single_price ?? item.unit_price ?? item.price, Number.NaN);
    const weight = toApiNumber(item.weight, 0);
    const qty = toApiNumber(item.quantity ?? item.qty, 0);
    const billing = weight > 0 ? weight : qty;
    if (Number.isFinite(price) && price > 0 && billing > 0) {
        return Math.round(price * billing * 100) / 100;
    }
    return Math.round(toApiNumber(item.total_price ?? item.subtotal, 0) * 100) / 100;
};

/** Sum of real product lines for an order (subtotal before commission). */
export const orderProductSubtotal = (orderOrLines = {}) => {
    if (Array.isArray(orderOrLines)) {
        return Math.round(orderOrLines.reduce((s, it) => s + orderProductLineAmount(it), 0) * 100) / 100;
    }
    const fromApi = toApiNumber(orderOrLines.product_subtotal, Number.NaN);
    if (Number.isFinite(fromApi)) {
        return Math.round(fromApi * 100) / 100;
    }
    const lines = orderOrLines.order_products || orderOrLines.items || orderOrLines.orderProducts || [];
    if (Array.isArray(lines) && lines.length) {
        return orderProductSubtotal(lines);
    }
    // Never use total − commission: denomination on the grand total warps subtotal.
    const commission = toApiNumber(orderOrLines.commission_amount, 0);
    if (commission > 0) return 0;
    return Math.round(toApiNumber(orderOrLines.total_price, 0) * 100) / 100;
};

/** Settings “Currency Display” modes (system settings). */
export const normalizeCurrencyDisplay = (raw) => {
    const v = String(raw || "code").trim().toLowerCase();
    if (v === "symbol" || v === "name" || v === "code") return v;
    if (v === "narrowsymbol" || v === "narrow_symbol") return "symbol";
    return "code";
};

/**
 * Curated symbols — browsers often have no real IQD glyph and keep printing "IQD"
 * even when currencyDisplay is "symbol" (settings label: د.ع).
 */
const CURRENCY_SYMBOLS = {
    IQD: "د.ع",
    USD: "$",
    EUR: "€",
    GBP: "£",
    TRY: "₺",
    AED: "د.إ",
    SAR: "ر.س",
    JOD: "د.ا",
    KWD: "د.ك",
};

const CURRENCY_NAMES = {
    IQD: { en: "Iraqi dinar", ar: "دينار عراقي", ku: "دیناری عێراقی", tr: "Irak dinarı" },
    USD: { en: "US dollar", ar: "دولار أمريكي", ku: "دۆلاری ئەمریکی", tr: "ABD doları" },
    EUR: { en: "Euro", ar: "يورو", ku: "یۆرۆ", tr: "Euro" },
    TRY: { en: "Turkish lira", ar: "ليرة تركية", ku: "لیرەی تورکی", tr: "Türk lirası" },
};

const getSystemNumberOptions = () => {
    const cfg = readCachedSystemConfig();
    return {
        locale: cfg?.locale || "en-US",
        currency_code: cfg?.currency_code || "IQD",
        currency_display: normalizeCurrencyDisplay(cfg?.currency_display),
        fraction_digits: Number.isFinite(cfg?.fraction_digits) ? cfg.fraction_digits : (cfg?.currency_code === "IQD" ? 0 : 2),
        language: (typeof document !== "undefined" && document.documentElement?.lang) || "en",
    };
};

const formatAmountNumber = (parsed, usedLocale, fractionDigits) =>
    parsed.toLocaleString(usedLocale, {
        useGrouping: true,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });

const resolveCurrencyName = (code, language) => {
    const map = CURRENCY_NAMES[code];
    if (!map) return null;
    const lang = String(language || "en").slice(0, 2).toLowerCase();
    return map[lang] || map.en;
};

/**
 * Format a number as a locale string with thousands separators
 * @param {number} number - The number to format
 * @param {string} locale - The locale to use for formatting (default: 'en-US')
 * @returns {string} - The formatted number as a string
 */
export const formatNumber = (number, locale) => {
    const parsed = toApiNumber(number, Number.NaN);
    if (!Number.isFinite(parsed)) {
        return "-";
    }
    const sys = getSystemNumberOptions();
    const usedLocale = locale || sys.locale;
    return parsed.toLocaleString(usedLocale, { useGrouping: true });
};

/**
 * Format a currency value respecting System Settings → Currency Display
 * (code / symbol / name).
 * @param {number} amount
 * @param {string} [currencyCode]
 * @param {string} [locale]
 * @returns {string}
 */
export const formatCurrency = (amount, currencyCode, locale) => {
    let parsed = toApiNumber(amount, Number.NaN);
    if (!Number.isFinite(parsed)) {
        return "-";
    }
    const sys = getSystemNumberOptions();
    const usedLocale = locale || sys.locale;
    const usedCurrency =
        currencyCode !== undefined && currencyCode !== null && String(currencyCode).trim() !== ""
            ? String(currencyCode).trim().toUpperCase()
            : (sys.currency_code || "IQD");
    const fractionDigits = Number.isFinite(sys.fraction_digits)
        ? sys.fraction_digits
        : (usedCurrency === "IQD" ? 0 : 2);
    const display = normalizeCurrencyDisplay(sys.currency_display);

    // Round to configured decimals and drop -0 / sub-cent drift.
    const factor = 10 ** fractionDigits;
    parsed = Math.round(parsed * factor) / factor + 0;
    const numberPart = formatAmountNumber(parsed, usedLocale, fractionDigits);

    // Explicit modes so IQD Symbol actually shows د.ع (Intl often still prints IQD).
    if (display === "symbol") {
        const curated = CURRENCY_SYMBOLS[usedCurrency];
        if (curated) {
            return `${numberPart} ${curated}`;
        }
        try {
            const intl = new Intl.NumberFormat(usedLocale, {
                style: "currency",
                currency: usedCurrency,
                currencyDisplay: "symbol",
                useGrouping: true,
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
            }).format(parsed);
            // If browser fell back to the ISO code, still prefer "amount CODE".
            if (intl.includes(usedCurrency) && !CURRENCY_SYMBOLS[usedCurrency]) {
                return `${numberPart} ${usedCurrency}`;
            }
            return intl;
        } catch {
            return `${numberPart} ${usedCurrency}`;
        }
    }

    if (display === "name") {
        const curatedName = resolveCurrencyName(usedCurrency, sys.language);
        if (curatedName) {
            return `${numberPart} ${curatedName}`;
        }
        try {
            return new Intl.NumberFormat(usedLocale, {
                style: "currency",
                currency: usedCurrency,
                currencyDisplay: "name",
                useGrouping: true,
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
            }).format(parsed);
        } catch {
            return `${numberPart} ${usedCurrency}`;
        }
    }

    // Prefer spaced "1,250,000 IQD" so separators stay readable in dense tables.
    return `${numberPart} ${usedCurrency}`;
};
